import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AiScoreResults from '../../../NewBlogSystem/EditComponents/Menus/AiScoreResults.jsx';
import {
  AiAssistantComposer,
  AiAssistantDecisionActions,
  AiAssistantMenuShell,
  AiAssistantQueue,
  AiAssistantTaskPicker,
} from '../../components/index.js';
import AiAssistantPreview from './AiAssistantPreview.jsx';
import { AiAssistantSteps } from './AiAssistantWorkflow.jsx';
import {
  AI_TASK_DETAILS,
  AI_TASK_TYPES,
  AI_WORKFLOW_PHASES,
} from '../utilities/aiAssistantState.js';

const attachTaskHeadings = (messages) => {
  let pendingTaskHeading = '';
  const entries = [];

  messages.forEach((message) => {
    if (message.role === 'task') {
      pendingTaskHeading = message.content;
      return;
    }

    entries.push({ ...message, taskHeading: pendingTaskHeading });
    pendingTaskHeading = '';
  });

  return { entries, pendingTaskHeading };
};

const PortfolioAssistantMenu = ({
  expanded,
  task,
  run,
  runs,
  messages,
  draft,
  busy,
  status,
  quotaExceeded,
  preview,
  confirmation,
  queueRequests = [],
  selectedItemLabel,
  onClose,
  onDraftChange,
  onSubmit,
  onRetry,
  onChooseTask,
  onImplementRecommendation,
  onExpandPreview,
  onConfirmPreview,
  onEditPreview,
  onDiscardPreview,
  onReturnToReview,
  onConfirmDecision,
  onRejectDecision,
  onQueueUpdate,
  onQueueRemove,
  onQueueRetry,
  onQueueMove,
  onQueueMoveBy,
}) => {
  const transcriptRef = useRef(null);
  const composerRef = useRef(null);
  const controlsRef = useRef(null);
  const nearLatestRef = useRef(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const displayTask = task || { type: AI_TASK_TYPES.GLOBAL_EDIT, label: 'Edit portfolio' };
  const details = AI_TASK_DETAILS[
    displayTask.type
  ] || AI_TASK_DETAILS[AI_TASK_TYPES.GLOBAL_EDIT];
  const promptRequired = details.promptRequired;
  const activeMessages = useMemo(() => messages.filter((message) => (
    run?.id ? (!message.runId || message.runId === run.id) : !message.runId
  )), [messages, run?.id]);
  const transcript = useMemo(() => attachTaskHeadings(activeMessages), [activeMessages]);
  const completedRuns = useMemo(() => (runs || []).filter((item) => (
    item.id !== run?.id && item.phase === AI_WORKFLOW_PHASES.DONE
  )), [run?.id, runs]);
  const latestCompletedScoreRunId = useMemo(() => (
    [...completedRuns].reverse().find(
      (item) => item.task?.type === AI_TASK_TYPES.SCORE_PORTFOLIO
    )?.id || ''
  ), [completedRuns]);
  const editingPreview = Boolean(
    preview
    && (run?.phase === AI_WORKFLOW_PHASES.REFINE || (
      !run && task?.type === AI_TASK_TYPES.REFINE_PREVIEW && task?.showComposer !== false
    ))
  );
  const showPreviewActions = Boolean(
    preview && !editingPreview && (!run || run.phase === AI_WORKFLOW_PHASES.REVIEW)
  );
  const showConfirmationActions = Boolean(
    confirmation && (!run || run.phase === AI_WORKFLOW_PHASES.CONFIRM)
  );
  const showComposer = Boolean(promptRequired);
  const taskOptions = useMemo(() => [
    ...(selectedItemLabel ? [{
      key: AI_TASK_TYPES.FOCUSED_EDIT,
      label: 'Edit selected item',
      iconClass: 'bi-pencil-square',
      title: `Edit ${selectedItemLabel} with AI`,
      promptRequired: true,
    }] : []),
    {
      key: AI_TASK_TYPES.GLOBAL_EDIT,
      label: 'Edit portfolio',
      iconClass: 'bi-pencil-square',
      promptRequired: true,
    },
    {
      key: AI_TASK_TYPES.SCORE_PORTFOLIO,
      label: 'Score portfolio',
      iconClass: 'bi-check2-circle',
      promptRequired: false,
    },
    {
      key: AI_TASK_TYPES.REGENERATE_PAGE,
      label: 'Regenerate page',
      iconClass: 'bi-arrow-clockwise',
      variant: 'btn-outline-secondary',
      promptRequired: true,
    },
  ], [selectedItemLabel]);
  const queueTaskOptions = useMemo(() => {
    const options = [...taskOptions];
    queueRequests.forEach((request) => {
      if (options.some((option) => option.key === request.task?.type)) return;
      const detail = AI_TASK_DETAILS[request.task?.type];
      options.push({
        key: request.task?.type,
        label: request.task?.label || detail?.label || 'AI request',
        promptRequired: detail?.promptRequired !== false,
      });
    });
    return options;
  }, [queueRequests, taskOptions]);
  const queuedRecommendationKeys = useMemo(() => queueRequests
    .filter((request) => request.task?.type === AI_TASK_TYPES.IMPLEMENT_RECOMMENDATION)
    .map((request) => `${request.task.scoreMessageId}:${request.task.recommendationIndex}`),
  [queueRequests]);

  const scrollToLatest = useCallback((behavior = 'smooth') => {
    const transcriptElement = transcriptRef.current;
    if (!transcriptElement) return;
    transcriptElement.scrollTo?.({ top: transcriptElement.scrollHeight, behavior });
    nearLatestRef.current = true;
    setShowJumpToLatest(false);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    if (!nearLatestRef.current) {
      setShowJumpToLatest(true);
      return;
    }
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    scrollToLatest(reduceMotion ? 'auto' : 'smooth');
  }, [busy, expanded, messages, preview, run?.phase, scrollToLatest, status]);

  useEffect(() => {
    if (!expanded || busy) return;
    if (showComposer) {
      composerRef.current?.focus();
      return;
    }
    if (run?.phase === AI_WORKFLOW_PHASES.REVIEW) {
      controlsRef.current
        ?.querySelector('[aria-label="Preview actions"] .btn-primary')
        ?.focus();
    } else if (run?.phase === AI_WORKFLOW_PHASES.CONFIRM) {
      controlsRef.current
        ?.querySelector('[aria-label="Confirmation actions"] .btn-danger, [aria-label="Confirmation actions"] .btn-primary')
        ?.focus();
    }
  }, [busy, expanded, run?.phase, showComposer, task?.key]);

  return (
    <AiAssistantMenuShell
      className="new-blog-side-menu ai-assistant-menu"
      title="AI Assistant"
      expanded={expanded}
      onClose={onClose}
      busy={busy}
      closeOnEscape={false}
    >
      <div className="ai-assistant-shell">
        <AiAssistantSteps run={run} />
        <div
          className="ai-assistant-transcript"
          ref={transcriptRef}
          role="log"
          aria-live="polite"
          aria-label="AI Assistant conversation"
          onScroll={(event) => {
            const element = event.currentTarget;
            const nearLatest = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
            nearLatestRef.current = nearLatest;
            if (nearLatest) setShowJumpToLatest(false);
          }}
        >
          {completedRuns.map((completedRun) => {
            const runMessages = attachTaskHeadings(messages.filter(
              (message) => message.runId === completedRun.id && message.role !== 'task'
            )).entries;
            return (
              <details
                className="ai-assistant-history"
                key={completedRun.id}
                open={completedRun.id === latestCompletedScoreRunId}
              >
                <summary>
                  <span>
                    <i className="bi bi-check2-circle" aria-hidden="true"></i>
                    {completedRun.task?.label || AI_TASK_DETAILS[completedRun.task?.type]?.label}
                  </span>
                  <small>{completedRun.outcome || 'Completed'}</small>
                </summary>
                <div className="ai-assistant-history-content">
                  {runMessages.map((message) => (
                    <div key={message.id} className={`is-${message.role}`}>
                      {message.content ? <p>{message.content}</p> : null}
                      {message.payload?.kind === 'score' ? (
                        <AiScoreResults
                          score={message.payload.score}
                          percentage={message.payload.percentage}
                          busy={quotaExceeded}
                          onImplement={onImplementRecommendation}
                          scoreMessageId={message.id}
                          queuedRecommendationKeys={queuedRecommendationKeys}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              </details>
            );
          })}

          {transcript.entries.length ? transcript.entries.map((message) => (
              <article
                className={`ai-assistant-message is-${message.role}`}
                key={message.id}
              >
                <div className="ai-assistant-message-avatar" aria-hidden="true">
                  <i className={`bi ${message.role === 'user' ? 'bi-person-fill' : 'bi-stars'}`}></i>
                </div>
                <div className="ai-assistant-message-content">
                  {message.taskHeading ? (
                    <header className="ai-assistant-message-task">{message.taskHeading}</header>
                  ) : null}
                  {message.payload?.kind === 'confirmation' ? (
                    <div className="ai-assistant-confirmation-message">
                      <strong>{message.payload.title}</strong>
                      <p>{message.content}</p>
                    </div>
                  ) : message.content ? <p>{message.content}</p> : null}
                  {message.payload?.kind === 'score' ? (
                    <AiScoreResults
                      score={message.payload.score}
                      percentage={message.payload.percentage}
                      busy={quotaExceeded}
                      onImplement={onImplementRecommendation}
                      scoreMessageId={message.id}
                      queuedRecommendationKeys={queuedRecommendationKeys}
                    />
                  ) : null}
                  {message.role === 'error' && message.retry ? (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      type="button"
                      onClick={() => onRetry(message.retry)}
                      disabled={busy || quotaExceeded}
                    >
                      <i className="bi bi-arrow-clockwise me-2" aria-hidden="true"></i>
                      Try again
                    </button>
                  ) : null}
                </div>
              </article>
          )) : (
            <div className="ai-assistant-welcome">
              <i className="bi bi-stars" aria-hidden="true"></i>
              <strong>How can I help with your portfolio?</strong>
              <span>Choose a task or describe the change you want to make.</span>
            </div>
          )}

          {busy && status ? (
            <article className="ai-assistant-message is-assistant is-working">
              <div className="ai-assistant-message-avatar" aria-hidden="true">
                <span className="spinner-border spinner-border-sm"></span>
              </div>
              <div className="ai-assistant-message-content">
                {transcript.pendingTaskHeading ? (
                  <header className="ai-assistant-message-task">
                    {transcript.pendingTaskHeading}
                  </header>
                ) : null}
                <p>{status}</p>
              </div>
            </article>
          ) : null}

          {preview ? (
            <AiAssistantPreview preview={preview} onExpand={onExpandPreview} />
          ) : null}

          {quotaExceeded ? (
            <div className="alert alert-warning small mb-0">
              AI features are temporarily unavailable because today&apos;s free allowance has been used.
            </div>
          ) : null}
        </div>

        <AiAssistantQueue
          requests={queueRequests}
          tasks={queueTaskOptions}
          disabled={quotaExceeded}
          onUpdate={onQueueUpdate}
          onRemove={onQueueRemove}
          onRetry={onQueueRetry}
          onMove={onQueueMove}
          onMoveBy={onQueueMoveBy}
        />

        {task?.type !== AI_TASK_TYPES.REFINE_PREVIEW ? (
          <AiAssistantTaskPicker
            tasks={taskOptions}
            activeTask={task?.type}
            onSelect={onChooseTask}
            disabled={quotaExceeded}
          />
        ) : null}

        <div ref={controlsRef} className="ai-assistant-controls">
          {showJumpToLatest ? (
            <button
              className="btn btn-sm btn-light ai-assistant-jump-latest"
              type="button"
              onClick={() => scrollToLatest()}
            >
              <i className="bi bi-arrow-down" aria-hidden="true"></i>
              Jump to latest
            </button>
          ) : null}

          {showPreviewActions ? (
            <AiAssistantDecisionActions
              ariaLabel="Preview actions"
              disabled={busy}
              actions={[
                { key: 'discard', label: 'Discard', iconClass: 'bi-x-lg', onClick: onDiscardPreview },
                {
                  key: 'edit', label: 'Request changes', iconClass: 'bi-stars', variant: 'outline-primary',
                  onClick: onEditPreview, disabled: !preview?.onRefine,
                },
                {
                  key: 'confirm', label: 'Confirm', iconClass: 'bi-check2', variant: 'primary',
                  onClick: onConfirmPreview, disabled: !preview?.summary?.hasChanges,
                },
              ]}
            />
          ) : null}

          {showConfirmationActions ? (
            <AiAssistantDecisionActions
              ariaLabel="Confirmation actions"
              disabled={busy}
              actions={[
                {
                  key: 'reject',
                  label: confirmation.cancelLabel || 'Reject',
                  iconClass: 'bi-x-lg',
                  onClick: onRejectDecision,
                },
                {
                  key: 'confirm',
                  label: confirmation.confirmLabel || 'Confirm',
                  iconClass: 'bi-check2',
                  variant: confirmation.variant === 'danger' ? 'danger' : 'primary',
                  onClick: onConfirmDecision,
                },
              ]}
            />
          ) : null}

          {showComposer ? (
            <>
              {editingPreview ? (
                <div className="ai-assistant-refine-navigation">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    type="button"
                    onClick={onReturnToReview}
                    disabled={busy}
                  >
                    <i className="bi bi-arrow-left me-2" aria-hidden="true"></i>
                    Back to review
                  </button>
                </div>
              ) : null}
              <AiAssistantComposer
                ref={composerRef}
                draft={draft}
                taskLabel={displayTask?.label || details.label}
                contextLabel={displayTask?.type === AI_TASK_TYPES.FOCUSED_EDIT
                  ? undefined
                  : displayTask?.contextLabel}
                placeholder={details.placeholder}
                busy={false}
                disabled={quotaExceeded}
                onDraftChange={onDraftChange}
                onSubmit={onSubmit}
              />
            </>
          ) : null}

        </div>
      </div>
    </AiAssistantMenuShell>
  );
};

PortfolioAssistantMenu.propTypes = {
  expanded: PropTypes.bool.isRequired,
  task: PropTypes.object,
  run: PropTypes.object,
  runs: PropTypes.arrayOf(PropTypes.object),
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  draft: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  status: PropTypes.string,
  quotaExceeded: PropTypes.bool,
  preview: PropTypes.object,
  confirmation: PropTypes.object,
  queueRequests: PropTypes.arrayOf(PropTypes.object),
  selectedItemLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDraftChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onChooseTask: PropTypes.func.isRequired,
  onImplementRecommendation: PropTypes.func.isRequired,
  onExpandPreview: PropTypes.func,
  onConfirmPreview: PropTypes.func,
  onEditPreview: PropTypes.func,
  onDiscardPreview: PropTypes.func,
  onReturnToReview: PropTypes.func,
  onConfirmDecision: PropTypes.func,
  onRejectDecision: PropTypes.func,
  onQueueUpdate: PropTypes.func.isRequired,
  onQueueRemove: PropTypes.func.isRequired,
  onQueueRetry: PropTypes.func.isRequired,
  onQueueMove: PropTypes.func.isRequired,
  onQueueMoveBy: PropTypes.func.isRequired,
};

export default PortfolioAssistantMenu;
